/**
 * 🧮 원가분석표 — 계산(순수 함수) (v2.234.0 · 2026-10-09)
 *
 * 사용자 지시: 「원가분석 메뉴 — 나라장터 원가산정처럼 매입목록에서 불러와서 원가 합산하고, 자유입력도 가능하게 하고,
 *   견적서 작성으로 바로 이어지게」 + 「매입 목록과 인포트렌드 가격표 중 선택해서 가져오기」.
 *
 * 줄 하나(CostLine)는 어디서 왔든(매입 목록 · 인포트렌드 가격표 · 직접 입력) 같은 모양이다 — 출처(source)와 기준(basis)만 다르다.
 *   · 매입 목록 줄: 최근 매입가(recent)·평균 매입가(avg) 를 들고 있고 basis 로 어느 쪽을 쓸지 고른다(직접 적을 수도).
 *   · 인포트렌드 줄: 가격표 공급가가 unitCost · 소비자가(listPrice)는 참고.
 *   · 직접 입력 줄: unitCost 를 손으로.
 * 판매 단가 = 원가 ÷ (1 − 마진율) 을 올림 단위로 올린 값 — 마진율은 **판매가 기준**(나라장터 BOM 과 같은 정의 · (판매−원가)÷판매).
 *   줄마다 판매 단가를 직접 적으면(sellUnit) 그 값이 이긴다(null 이면 자동).
 * 저장은 서버(jsonb) — 화면은 이 파일의 함수로만 숫자를 만든다(그물 scripts/check-cost-sheet.ts 가 같은 함수를 검사한다).
 */
import type { QuoteDraft } from './quote-api';

export type CostSource = 'purchase' | 'infortrend' | 'manual';
export type CostBasis = 'recent' | 'avg' | 'manual';

export interface CostLine {
  /** 화면 key (저장도 된다 — 줄을 구분하는 데만 쓴다) */
  key: string;
  source: CostSource;
  /** 품목 마스터 코드 (매입 목록 줄) */
  itemCode: string | null;
  /** 인포트렌드 P/N */
  pn: string | null;
  name: string;
  spec: string;
  vendor: string;
  qty: number;
  /** 적용 원가 단가 — basis 가 recent/avg 면 그 값을 복사해 둔 것(표시·저장용) · manual 이면 손으로 적은 값 */
  unitCost: number;
  recent: number | null;
  avg: number | null;
  basis: CostBasis;
  /** 인포트렌드 소비자가 (참고) */
  listPrice: number | null;
  /** 인포트렌드 가격표 분기 (예: 2026 Q3) */
  quarter: string | null;
  /** 판매 단가 직접 지정 — null 이면 마진율로 자동 */
  sellUnit: number | null;
  note: string;
}

export interface CostSheetParams {
  marginPct: number;
  roundUnit: number;
}

export const ROUND_UNITS = [1, 10, 100, 1000, 10000] as const;
/** 마진율 상한 — 100% 이상이면 「원가 ÷ (1 − 마진율)」 이 터진다. 서버 CHECK(< 100) 와 같은 뜻 */
export const MARGIN_MAX = 99;
export const SOURCE_LABEL: Readonly<Record<CostSource, string>> = { purchase: '매입', infortrend: '인포트렌드', manual: '직접' };

let keySeq = 0;
export function newLineKey(): string {
  keySeq += 1;
  return `${Date.now().toString(36)}-${keySeq}-${Math.random().toString(36).slice(2, 6)}`;
}

export function emptyLine(source: CostSource = 'manual'): CostLine {
  return {
    key: newLineKey(), source, itemCode: null, pn: null, name: '', spec: '', vendor: '', qty: 1, unitCost: 0,
    recent: null, avg: null, basis: 'manual', listPrice: null, quarter: null, sellUnit: null, note: '',
  };
}

const num = (v: unknown, d = 0): number => (typeof v === 'number' && Number.isFinite(v) ? v : typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v)) ? Number(v) : d);
const numOrNull = (v: unknown): number | null => (v === null || v === undefined || v === '' ? null : Number.isFinite(Number(v)) ? Number(v) : null);
const str = (v: unknown): string => (typeof v === 'string' ? v : v === null || v === undefined ? '' : String(v));

/** 서버(jsonb)에서 온 줄을 화면 모양으로 — 빠진 칸·이상한 값은 조용히 기본값으로 (옛 저장분과 호환) */
export function normalizeLine(x: unknown): CostLine {
  const o = (x && typeof x === 'object' ? x : {}) as Record<string, unknown>;
  const source: CostSource = o.source === 'purchase' || o.source === 'infortrend' ? o.source : 'manual';
  const basis: CostBasis = o.basis === 'recent' || o.basis === 'avg' ? o.basis : 'manual';
  return {
    key: str(o.key) || newLineKey(),
    source,
    itemCode: str(o.itemCode) || null,
    pn: str(o.pn) || null,
    name: str(o.name),
    spec: str(o.spec),
    vendor: str(o.vendor),
    qty: Math.max(0, num(o.qty, 1)),
    unitCost: Math.max(0, num(o.unitCost, 0)),
    recent: numOrNull(o.recent),
    avg: numOrNull(o.avg),
    basis,
    listPrice: numOrNull(o.listPrice),
    quarter: str(o.quarter) || null,
    sellUnit: numOrNull(o.sellUnit),
    note: str(o.note),
  };
}

/** 적용 원가 단가 — 기준(최근/평균/직접)에 따라 */
export function appliedUnitCost(l: CostLine): number {
  if (l.basis === 'recent' && l.recent !== null) return l.recent;
  if (l.basis === 'avg' && l.avg !== null) return l.avg;
  return l.unitCost;
}

/** 줄 원가 금액 = 수량 × 적용 단가 (원 단위 반올림) */
export function lineCost(l: CostLine): number {
  return Math.round(l.qty * appliedUnitCost(l));
}

/**
 * 마진율로 판매 단가 만들기 — 원가 ÷ (1 − 마진율%) 을 올림 단위로 **올림**.
 *   원가 0 이면 0 · 마진율은 0~MARGIN_MAX 로 잘라 쓴다 · 올림은 부동소수 오차(12.0000001 → 13)를 막으려고 1e-9 를 뺀다.
 */
export function autoSellUnit(unitCost: number, marginPct: number, roundUnit: number): number {
  if (!(unitCost > 0)) return 0;
  const m = Math.min(MARGIN_MAX, Math.max(0, Number.isFinite(marginPct) ? marginPct : 0));
  const unit = ROUND_UNITS.includes(roundUnit as (typeof ROUND_UNITS)[number]) ? roundUnit : 1;
  const raw = unitCost / (1 - m / 100);
  return Math.ceil(raw / unit - 1e-9) * unit;
}

/** 줄 판매 단가 — 직접 적은 값이 있으면 그것, 없으면 마진율 자동 */
export function lineSellUnit(l: CostLine, p: CostSheetParams): number {
  return l.sellUnit !== null ? l.sellUnit : autoSellUnit(appliedUnitCost(l), p.marginPct, p.roundUnit);
}

export function lineSell(l: CostLine, p: CostSheetParams): number {
  return Math.round(l.qty * lineSellUnit(l, p));
}

/** 줄 마진율(판매가 기준 %) — 판매 금액이 0 이면 null */
export function lineMarginPct(l: CostLine, p: CostSheetParams): number | null {
  const sell = lineSell(l, p);
  if (sell <= 0) return null;
  return ((sell - lineCost(l)) / sell) * 100;
}

export interface SheetTotals {
  cost: number;
  sell: number;
  margin: number;
  /** 판매가 기준 마진율 % · 판매 0 이면 null */
  marginPct: number | null;
  /** 부가세 포함 판매가 (10%) */
  sellWithVat: number;
  lineCount: number;
}

export function sheetTotals(lines: readonly CostLine[], p: CostSheetParams): SheetTotals {
  let cost = 0;
  let sell = 0;
  for (const l of lines) {
    cost += lineCost(l);
    sell += lineSell(l, p);
  }
  const margin = sell - cost;
  return { cost, sell, margin, marginPct: sell > 0 ? (margin / sell) * 100 : null, sellWithVat: Math.round(sell * 1.1), lineCount: lines.length };
}

/**
 * 견적서 초안 — 원가분석표의 줄을 견적서 품목으로(판매 단가 = 줄 판매 단가).
 *   이름이 빈 줄과 수량 0 줄은 빼고 넘긴다(견적서에 빈 줄이 생기지 않게) — 빠진 줄 수를 같이 돌려준다.
 */
export function toQuoteDraft(sheet: { title: string; customer: string; lines: readonly CostLine[] } & CostSheetParams): { draft: QuoteDraft; skipped: number } {
  const usable = sheet.lines.filter((l) => l.name.trim() !== '' && l.qty > 0);
  const draft: QuoteDraft = {
    quoteDate: null,
    recipientCompany: sheet.customer,
    reference: '',
    projectName: sheet.title,
    empName: '',
    contactPhone: '',
    contactEmail: '',
    items: usable.map((l) => ({
      itemName: l.name.trim(),
      description: l.spec,
      qty: l.qty,
      unitPrice: lineSellUnit(l, sheet),
      note: l.note,
      months: 1,
      // 원가가 따라간다(화면 참고용 · 저장 안 됨) — 작성 창이 단가를 고치는 동안 줄 마진율을 보여 준다 (v2.235.0)
      costUnit: appliedUnitCost(l),
    })),
  };
  return { draft, skipped: sheet.lines.length - usable.length };
}

/* ────────────────────────────────────────────────────────────────
 * 제안가 정하기 도우미 (v2.235.0 · 2026-10-09) — 사용자 「견적서 쓸 때 제안가격 참고」
 * ──────────────────────────────────────────────────────────────── */

/** 마진율 시나리오 띠 — 이 마진율들로 바꾸면 총 판매가가 얼마가 되나 */
export const SCENARIO_MARGINS = [10, 15, 20, 25, 30] as const;

/** 같은 줄로 마진율만 바꿨을 때 총 판매가(공급가) — 줄마다 직접 적은 판매 단가는 그대로 둔다 */
export function sellAtMargin(lines: readonly CostLine[], roundUnit: number, marginPct: number): number {
  return sheetTotals(lines, { marginPct, roundUnit }).sell;
}

export type TargetSolve =
  | { ok: true; marginPct: number; sell: number }
  | {
      ok: false;
      /** no_lines 넣을 줄이 없다 · fixed_only 전부 직접 적은 단가라 마진율로 못 움직인다 · below_cost 0% 로도 목표보다 크다 · unreachable 99% 로도 못 미친다 */
      reason: 'no_lines' | 'fixed_only' | 'below_cost' | 'unreachable';
      /** 그때의 총 판매가(below_cost 는 0% · unreachable 은 99% · fixed_only 는 지금 값) */
      sell: number;
    };

/**
 * 목표 금액 맞추기 — 총 판매가(공급가)가 목표 **이상**이 되는 가장 작은 마진율(0.01% 단위).
 *   판매 단가가 올림 단위로 올라가므로 딱 맞는 마진율은 보통 없다 → 「넘는 쪽에서 가장 가까운」 값을 돌려준다.
 *   총 판매가는 마진율이 오를수록 커지기만 하므로(올림은 단조) 이분 탐색으로 찾는다.
 */
export function solveMarginForTarget(lines: readonly CostLine[], roundUnit: number, targetSell: number): TargetSolve {
  const sellAt = (m: number): number => sellAtMargin(lines, roundUnit, m);
  if (lines.length === 0 || !(targetSell > 0)) return { ok: false, reason: 'no_lines', sell: 0 };
  const movable = lines.some((l) => l.sellUnit === null && appliedUnitCost(l) > 0 && l.qty > 0);
  if (!movable) return { ok: false, reason: 'fixed_only', sell: sellAt(0) };
  const lo = sellAt(0);
  if (targetSell < lo) return { ok: false, reason: 'below_cost', sell: lo };
  const hi = sellAt(MARGIN_MAX);
  if (targetSell > hi) return { ok: false, reason: 'unreachable', sell: hi };
  let a = 0;
  let b = MARGIN_MAX * 100; // 0.01% 단위
  while (a < b) {
    const mid = Math.floor((a + b) / 2);
    if (sellAt(mid / 100) >= targetSell) b = mid;
    else a = mid + 1;
  }
  const marginPct = a / 100;
  return { ok: true, marginPct, sell: sellAt(marginPct) };
}

/**
 * 기존 견적서 → 원가분석표 줄 (v2.236.0 · 「이 견적서의 원가 보기」)
 *   판매 단가는 견적서 값으로 **고정**(sellUnit) · 원가는 모름(0 · 직접) — 매입 원가는 화면이 품목 마스터 이름이 같을 때만 채운다.
 *   품목명이 비면 상세내용 첫 줄을 이름으로 · 품목명·상세내용이 다 빈 줄은 뺀다 · 음수 단가(할인 줄)는 0 으로.
 *   costUnit(작성 중인 견적서 줄이 💡 가격 참고로 알던 원가 · v2.248.0)이 있으면 그 값이 원가(직접).
 */
export function linesFromQuoteItems(
  items: readonly { itemName: string; description: string; qty: number; unitPrice: number; note: string; costUnit?: number | null }[],
): CostLine[] {
  return items
    .filter((it) => it.itemName.trim() !== '' || it.description.trim() !== '')
    .map((it) => ({
      ...emptyLine('manual'),
      name: it.itemName.trim() || (it.description.trim().split('\n')[0] ?? '').trim(),
      spec: it.description,
      qty: Math.max(0, Number.isFinite(it.qty) ? it.qty : 0),
      unitCost: it.costUnit != null && Number.isFinite(it.costUnit) && it.costUnit > 0 ? it.costUnit : 0,
      sellUnit: Math.max(0, Number.isFinite(it.unitPrice) ? it.unitPrice : 0),
      note: it.note,
    }));
}

/** 아직 손대지 않은 빈 표인가 — [+ 새 원가분석] 을 두 번 눌러 빈 표가 쌓이지 않게(운영 10-09 18:48 빈 표 2개) */
export function isBlankSheet(
  s: { title: string; customer: string; memo: string; lines: readonly CostLine[]; quoteId: number | null },
  defaultTitle: string,
): boolean {
  return s.title === defaultTitle && s.lines.length === 0 && s.customer.trim() === '' && s.memo.trim() === '' && s.quoteId === null;
}

export const fmtKrw = (n: number | null | undefined): string =>
  n === null || n === undefined || !Number.isFinite(n) ? '—' : Math.round(n).toLocaleString('ko-KR');
export const fmtPct = (n: number | null | undefined, digits = 1): string =>
  n === null || n === undefined || !Number.isFinite(n) ? '—' : `${n.toFixed(digits)}%`;
